'use client';

import { useState, useCallback } from 'react';
import { tryCatch, type Result, type AppError } from '@shared/utils';
import type { ResultState } from '../types/ResultState';
import type { UseResultOptions } from '../types/UseResultOptions';
import type { UseResultCallbackReturn } from '../types/UseResultCallbackReturn';
import { useNotification } from './useNotification';

const INITIAL_STATE = <T, E>(): ResultState<T, E> => ({
  data: null,
  error: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
});

/**
 * @group Hooks
 * @summary Hook for wrapping synchronous callbacks with the Result pattern.
 * Useful for form validation, parsing, and other sync operations.
 *
 * @example
 * ```tsx
 * function FormComponent() {
 *   const { execute: validate, state } = useResultCallback(
 *     (data: FormData) => {
 *       const result = validateSchema(data);
 *       if (!result.valid) throw new Error(result.errors[0]);
 *       return result.data;
 *     },
 *     {
 *       notificationTitle: 'Validation Error',
 *       resource: 'form.validation'
 *     }
 *   );
 *
 *   const handleSubmit = (data: FormData) => {
 *     const result = validate(data);
 *     if (result.isOk) {
 *       // Proceed with valid data
 *       submitForm(result.getValue());
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {state.isError && <ErrorMessage>{state.error.errorMessage}</ErrorMessage>}
 *       ...
 *     </form>
 *   );
 * }
 * ```
 */
export function useResultCallback<T, TArgs extends unknown[], E = AppError>(
  fn: (...args: TArgs) => T,
  options: UseResultOptions<T, E> = {}
): UseResultCallbackReturn<T, TArgs, E> {
  const [state, setState] = useState<ResultState<T, E>>(INITIAL_STATE);
  const { notify } = useNotification();

  const execute = useCallback((...args: TArgs): Result<T, E> => {
    const result = tryCatch<T, E>(() => fn(...args));

    result.match({
      ok: (data) => {
        setState({ data, error: null, isLoading: false, isSuccess: true, isError: false });
        options.onSuccess?.(data);
      },
      err: (error) => {
        setState({ data: null, error, isLoading: false, isSuccess: false, isError: true });
        options.onError?.(error);

        // Show notification unless explicitly suppressed
        if (options.showNotification !== false) {
          const appError = error as AppError;
          notify({
            title: options.notificationTitle || 'Error',
            message: appError.errorMessage || 'An unexpected error occurred',
            variant: options.notificationVariant || 'error',
            error: {
              error: appError,
              resource: options.resource || 'result.callback',
            },
          });
        }
      }
    });

    return result;
  }, [fn, notify, options]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return { execute, state, reset };
}
